/**
 * AVL element id → channel.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  TRANSCRIBE, NEVER INFER.
 * ─────────────────────────────────────────────────────────────────────────────
 * Every row below comes from `AVL_ID_REFERENCE.md` in this directory or from the
 * sourced transcription folded into it. Nothing is here because it "looks like" the
 * neighbouring id, because another family uses that number, or because a value would
 * be convenient. An id whose width, scaling or signedness could not be sourced is
 * listed in `AVL_DELIBERATELY_UNMAPPED` with the reason, and decodes to raw bytes —
 * which is recoverable — instead of to a plausible wrong number, which is not.
 *
 * The reference's three binding rules, and how this file obeys them:
 *
 *  (a) **Width comes from the Codec 8 size group in the packet, never from a table.**
 *      `bytes` on a descriptor is an advisory cross-check only: the decoder reads
 *      exactly the bytes the framing handed it and reports `widthMismatch` when the
 *      table disagreed. The vendor tables contradict each other on widths (72–75,
 *      205, 245), so trusting them over the packet desynchronises the parse.
 *
 *  (b) **The map is keyed by (family, firmware, id), never by id alone.** A flat
 *      `Record<number, Descriptor>` is provably wrong: on FMB/FMC 251 is Idling, on
 *      FM11XY/FM36/FM64 it is Immobilizer; 252 is Unplug here and Authorized Driving
 *      there; 8 is an 8-byte iButton here and a 2-byte ×0.1 °C probe on FM64. Only
 *      `fmb_fmc` — the FMC150's family — has decode rows. Other families resolve to
 *      nothing and fall through to raw storage, because a half-populated table for a
 *      family we cannot test is worse than no table: raw is recoverable, a silently
 *      mislabelled odometer is not. `AVL_FAMILY_COLLISIONS` documents the traps.
 *
 *  (c) **Every unrecognised (id, raw bytes) pair is persisted.** Unknown ids are
 *      skipped by length from the framing, so a mapping gap throws nothing and logs
 *      nothing — it is invisible. `channelKeyForUnmappedAvlId()` turns it into a
 *      first-class channel so the gap becomes a backfill rather than a hole.
 *
 * Headline corrections carried from the reference: id 89 is fuel level in PERCENT
 * (litres is 84), id 132 is 8 bytes not 4, id 102 is MINUTES and needs
 * ALL-CAN300/CANCONTROL, id 110 is l/h at raw ÷ 10.
 */
import { CAN_STATE_FLAGS, getChannel, isOutOfRange } from './registry.js';
export const AVL_FAMILIES = ['fmb_fmc', 'fm11xy', 'fm36', 'fm64', 'tmt'];
/**
 * Families this map can decode. Deliberately one entry: RIVENA ships FMC150s and
 * has no way to bench-test another family's element list. Everything else decodes to
 * raw, which loses nothing.
 */
export const DECODABLE_AVL_FAMILIES = ['fmb_fmc'];
/**
 * Model string → family. Unknown models return null rather than defaulting to
 * `fmb_fmc`: guessing the family is exactly the mistake rule (b) exists to prevent,
 * and an unknown model decoding to raw stays recoverable.
 */
export function familyForDeviceModel(model) {
    if (!model)
        return null;
    const normalized = model.trim().toUpperCase();
    if (/^FM[BC](6[45]0|64[01])/.test(normalized))
        return 'fm64';
    if (/^FMM6[45]0/.test(normalized))
        return 'fm64';
    if (/^FM[BC]/.test(normalized))
        return 'fmb_fmc';
    if (/^FMM/.test(normalized))
        return 'fmb_fmc';
    if (/^FM36/.test(normalized))
        return 'fm36';
    if (/^FM11/.test(normalized))
        return 'fm11xy';
    if (/^TMT/.test(normalized))
        return 'tmt';
    return null;
}
/**
 * Geofence zones 01–50. The ids are scattered across the id space rather than
 * contiguous, which is exactly the kind of thing a "clever" range check gets wrong,
 * so the list is written out. Transcribed from ioe/FMBXY/FMC003 (3/3 agreement).
 */
const GEOFENCE_ZONE_AVL_IDS = [
    155, 156, 157, 158, 159, 61, 62, 63, 64, 65, 70, 88, 91, 92, 93, 94, 95, 96, 97, 98, 99, 153, 154,
    190, 191, 192, 193, 194, 195, 196, 197, 198, 208, 209, 216, 217, 218, 219, 220, 221, 222, 223,
    224, 225, 226, 227, 228, 229, 230, 231,
];
function geofenceZoneDescriptors() {
    return GEOFENCE_ZONE_AVL_IDS.map((avlId, index) => ({
        avlId,
        channel: `geofence_zone_${String(index + 1).padStart(2, '0')}`,
        bytes: 1,
    }));
}
/**
 * The FMB/FMC table — the only decodable one. Grouped the way the vendor groups them
 * so a row can be found next to its neighbours when checking against the source.
 */
const FMB_FMC_DESCRIPTORS = [
    // ── Permanent I/O ─────────────────────────────────────────────────────────
    { avlId: 1, channel: 'digital_input_1', bytes: 1 },
    { avlId: 2, channel: 'digital_input_2', bytes: 1 },
    { avlId: 3, channel: 'digital_input_3', bytes: 1 },
    { avlId: 4, channel: 'pulse_counter_din1', bytes: 4 },
    { avlId: 9, channel: 'analog_input_1', bytes: 2, divisor: 1000 },
    { avlId: 12, channel: 'fuel_used_gps', bytes: 4, divisor: 1000 },
    { avlId: 16, channel: 'total_odometer', bytes: 4 },
    { avlId: 21, channel: 'gsm_signal', bytes: 1 },
    { avlId: 24, channel: 'speed_gps', bytes: 2 },
    { avlId: 66, channel: 'external_voltage', bytes: 2, divisor: 1000 },
    { avlId: 67, channel: 'battery_voltage', bytes: 2, divisor: 1000 },
    { avlId: 68, channel: 'battery_current', bytes: 2, divisor: 1000 },
    { avlId: 69, channel: 'gnss_status', bytes: 1 },
    { avlId: 80, channel: 'data_mode', bytes: 1 },
    { avlId: 113, channel: 'battery_level', bytes: 1 },
    { avlId: 179, channel: 'digital_output_1', bytes: 1 },
    { avlId: 180, channel: 'digital_output_2', bytes: 1 },
    { avlId: 181, channel: 'gnss_pdop', bytes: 2, divisor: 10 },
    { avlId: 182, channel: 'gnss_hdop', bytes: 2, divisor: 10 },
    { avlId: 199, channel: 'trip_odometer', bytes: 4 },
    { avlId: 200, channel: 'sleep_mode', bytes: 1 },
    { avlId: 205, channel: 'gsm_cell_id', bytes: 2 },
    { avlId: 206, channel: 'gsm_area_code', bytes: 2 },
    { avlId: 237, channel: 'network_type', bytes: 1 },
    { avlId: 239, channel: 'ignition', bytes: 1 },
    { avlId: 240, channel: 'movement', bytes: 1 },
    { avlId: 241, channel: 'gsm_operator', bytes: 4 },
    // ── 1-Wire probes ─────────────────────────────────────────────────────────
    // Signed and ×0.1: a −18 °C reefer read unsigned becomes +6553.5 °C.
    { avlId: 72, channel: 'dallas_temperature_1', bytes: 4, signed: true, divisor: 10 },
    { avlId: 73, channel: 'dallas_temperature_2', bytes: 4, signed: true, divisor: 10 },
    { avlId: 74, channel: 'dallas_temperature_3', bytes: 4, signed: true, divisor: 10 },
    { avlId: 75, channel: 'dallas_temperature_4', bytes: 4, signed: true, divisor: 10 },
    { avlId: 76, channel: 'dallas_id_1', bytes: 8 },
    { avlId: 77, channel: 'dallas_id_2', bytes: 8 },
    { avlId: 79, channel: 'dallas_id_3', bytes: 8 },
    { avlId: 71, channel: 'dallas_id_4', bytes: 8 },
    // ── OBD ───────────────────────────────────────────────────────────────────
    { avlId: 30, channel: 'obd_dtc_count', bytes: 1 },
    { avlId: 31, channel: 'engine_load', bytes: 1 },
    { avlId: 32, channel: 'coolant_temperature', bytes: 1, signed: true },
    // RPM, speed and fuel level as the FMC150's OBD-II reader reports them — standard
    // PIDs 0x0C, 0x0D and 0x2F, already converted on the device (hence RPM's documented
    // maximum of 16 384: the PID's 65 535 ÷ 4). Their OWN channels, never the CAN ones:
    // the capability profile reads `engine_rpm` & co. as evidence of a CAN adapter, and an
    // OBD value written there would report a CAN bus that is not fitted. The live view
    // falls back from CAN to OBD explicitly instead.
    //
    // 36 used to sit in AVL_DELIBERATELY_UNMAPPED as "collision table only". The collision
    // is with FM64, where 36 is Total Mileage; the FMB/FMC column of that same table
    // (AVL_ID_REFERENCE §6) names it Engine RPM, 2 bytes. Decoding is per family, so the
    // FM64 meaning cannot reach an FMC150. Each channel has a registry range: a scaling
    // error would surface as out_of_range rows, never as a plausible wrong dial.
    { avlId: 36, channel: 'obd_engine_rpm', bytes: 2 },
    { avlId: 37, channel: 'obd_vehicle_speed', bytes: 1 },
    { avlId: 48, channel: 'obd_fuel_level_pct', bytes: 1 },
    { avlId: 49, channel: 'distance_since_codes_clear', bytes: 2 },
    { avlId: 51, channel: 'control_module_voltage', bytes: 2, divisor: 1000 },
    { avlId: 53, channel: 'ambient_air_temperature', bytes: 1, signed: true },
    // ── CAN (LV-CAN200 / ALL-CAN300 / CANCONTROL) ─────────────────────────────
    { avlId: 81, channel: 'vehicle_speed_can', bytes: 1 },
    { avlId: 82, channel: 'accelerator_pedal', bytes: 1 },
    { avlId: 83, channel: 'fuel_consumed_total', bytes: 4, divisor: 10 },
    // 84 is LITRES (÷10); 89 is PERCENT. Swapping them makes an 80 L tank read "100 L"
    // when full — plausible, never throws, and fatal to a fuel-theft module.
    { avlId: 84, channel: 'fuel_level_l', bytes: 2, divisor: 10 },
    { avlId: 85, channel: 'engine_rpm', bytes: 2 },
    { avlId: 87, channel: 'total_mileage_can', bytes: 4 },
    { avlId: 89, channel: 'fuel_level_pct', bytes: 1 },
    { avlId: 90, channel: 'door_status', bytes: 2 },
    { avlId: 100, channel: 'program_number', bytes: 4 },
    { avlId: 101, channel: 'can_module_id', bytes: 8 },
    // Minutes. Storing it as hours or seconds is a 60× error that still looks
    // approximately reasonable on a dashboard, which is why it gets a comment.
    { avlId: 102, channel: 'engine_worktime', bytes: 4 },
    { avlId: 103, channel: 'engine_worktime_counted', bytes: 4 },
    { avlId: 107, channel: 'fuel_consumed_counted', bytes: 4, divisor: 10 },
    // l/h, not l/100km. The two diverge completely at idle, which is the regime
    // engins BTP spend most of their day in.
    { avlId: 110, channel: 'fuel_rate', bytes: 2, divisor: 10 },
    { avlId: 115, channel: 'engine_temperature', bytes: 2, signed: true, divisor: 10 },
    { avlId: 123, channel: 'control_state_flags', bytes: 4 },
    // Eight bytes, not four. A four-byte registration looks in the wrong Codec 8 size
    // group and silently swallows a neighbouring element instead.
    { avlId: 132, channel: 'security_state_flags', bytes: 8 },
    // ── Driver identification ─────────────────────────────────────────────────
    // Big-endian hex is this platform's convention for opaque keys. Traccar renders
    // id 78 little-endian (readLongLE + %016X); the two never match, so a key imported
    // from a Traccar-based system must be byte-reversed before it is compared.
    { avlId: 78, channel: 'ibutton_id', bytes: 8 },
    { avlId: 8, channel: 'ibutton_authorized', bytes: 8, note: 'FMB only — FM64 uses 8 for a probe' },
    { avlId: 207, channel: 'rfid_id', bytes: 8 },
    // 238 is named "User ID" by Teltonika and is NOT a person: both scrapes describe it
    // as the MAC of a Bluetooth NMEA receiver. Binding it to a driver attributes trips
    // to a dongle.
    { avlId: 238, channel: 'ble_nmea_receiver_mac', bytes: 8 },
    { avlId: 147, channel: 'driver1_id_high', bytes: 8 },
    { avlId: 148, channel: 'driver1_id_low', bytes: 8 },
    { avlId: 149, channel: 'driver2_id_high', bytes: 8 },
    { avlId: 150, channel: 'driver2_id_low', bytes: 8 },
    { avlId: 403, channel: 'driver_name', bytes: 35 },
    { avlId: 404, channel: 'driver_card_license_type', bytes: 1 },
    { avlId: 405, channel: 'driver_gender', bytes: 1 },
    { avlId: 409, channel: 'driver_status_event', bytes: 1 },
    // Variable-length: Codec 8 Extended only, so `bytes` is deliberately absent.
    { avlId: 264, channel: 'barcode_id' },
    { avlId: 385, channel: 'beacon_list' },
    { avlId: 548, channel: 'beacon_advanced' },
    // ── Eventual I/O — alarms and events ──────────────────────────────────────
    { avlId: 236, channel: 'alarm', bytes: 1 },
    { avlId: 243, channel: 'green_driving_duration', bytes: 2 },
    {
        avlId: 244,
        channel: 'din2_ain2_event',
        bytes: 1,
        note: 'HW support FMB1xx; absent from FMC003',
    },
    // Width is contradictory in the only source (8 declared, 4 described). Rule (a)
    // makes that harmless — the framing supplies the width — but the packed Z/Y/X
    // bytes are left opaque rather than split into three invented numbers.
    { avlId: 245, channel: 'gyroscope_axis' },
    { avlId: 246, channel: 'towing', bytes: 1 },
    { avlId: 247, channel: 'crash_detection', bytes: 1 },
    { avlId: 248, channel: 'immobilizer', bytes: 1 },
    { avlId: 249, channel: 'jamming', bytes: 1 },
    // Base firmware knows trip start/stop only; 2–9 (business / private / custom)
    // arrive with 01.00.24 and the Bluetooth app. A 3 on older firmware is therefore
    // out of range, and worth seeing rather than silently accepting.
    { avlId: 250, channel: 'trip_event', bytes: 1, valueRange: { min: 0, max: 1 } },
    {
        avlId: 250,
        channel: 'trip_event',
        bytes: 1,
        firmwareMin: '01.00.24',
        valueRange: { min: 0, max: 9 },
    },
    { avlId: 251, channel: 'idling', bytes: 1 },
    { avlId: 252, channel: 'unplug', bytes: 1 },
    { avlId: 253, channel: 'green_driving_type', bytes: 1 },
    // ÷100, not ÷10. prakharsr's parser says g*10 and is wrong by a factor of ten
    // against three Teltonika-derived scrapes; a 1.23 g braking event becomes 12.3 g,
    // which no vehicle survives and every scoring model would treat as a crash.
    { avlId: 254, channel: 'green_driving_value', bytes: 1, divisor: 100 },
    { avlId: 255, channel: 'over_speeding', bytes: 1 },
    { avlId: 175, channel: 'auto_geofence', bytes: 1 },
    ...geofenceZoneDescriptors(),
    // ── Codec 8 Extended id space (id > 255) ──────────────────────────────────
    // Two-byte ids: these are unreachable on plain Codec 8, and 256/257/281 arrive only
    // in the extended block's variable-length ("X byte") section, which is why several
    // rows deliberately carry no `bytes` — the wire supplies the length.
    //
    // Transcribed from the Phase 4 research pass (FMC003 wiki scrape + Traccar +
    // filipkroca). Only rows the research grades high or medium are here; anything it
    // grades low, or whose units it could not pin, is in AVL_DELIBERATELY_UNMAPPED with
    // the reason, and lands as io_<id> with its bytes intact.
    { avlId: 256, channel: 'vin' },
    { avlId: 257, channel: 'crash_trace' },
    // ASCII, comma-separated, already-formatted mnemonics. Three sources agree, and
    // Traccar's only transform is swapping the comma for a space. See dtc.ts.
    { avlId: 281, channel: 'dtc_codes' },
    { avlId: 283, channel: 'driving_state', bytes: 1 },
    { avlId: 284, channel: 'driving_records', bytes: 2 },
    { avlId: 303, channel: 'instant_movement', bytes: 1 },
    { avlId: 304, channel: 'range_on_battery', bytes: 4 },
    { avlId: 305, channel: 'range_on_additional_fuel', bytes: 4 },
    { avlId: 317, channel: 'crash_event_counter', bytes: 1 },
    { avlId: 325, channel: 'vin_can', bytes: 17 },
    { avlId: 380, channel: 'digital_output_3', bytes: 1 },
    { avlId: 381, channel: 'ground_sense', bytes: 1 },
    // KILOMETRES, and 4 bytes. Neighbouring odometers are metres (16, 87): the mileage
    // report compares all three, so a unit swap here shows up as a 1000× drift.
    { avlId: 389, channel: 'oem_total_mileage', bytes: 4 },
    { avlId: 390, channel: 'oem_fuel_level_l', bytes: 4, divisor: 10 },
    { avlId: 391, channel: 'private_mode', bytes: 1 },
    { avlId: 402, channel: 'oem_distance_until_service', bytes: 4 },
    { avlId: 410, channel: 'oem_battery_charge_state', bytes: 1 },
    { avlId: 411, channel: 'oem_battery_charge_level', bytes: 1 },
    { avlId: 412, channel: 'oem_battery_power_consumption', bytes: 2 },
    // SECONDS. engine_worktime (102) is minutes; the two are adjacent on the vehicle
    // page and a swap is a 60× error in the direction nobody notices.
    { avlId: 449, channel: 'ignition_on_counter', bytes: 4 },
    // Protocol-4 flag words: 8-byte bit vectors, kept whole. The bit legend is an
    // explicit gap, and the same firmware emits every bit as its own id below.
    { avlId: 517, channel: 'state_flags_p4_security', bytes: 8 },
    { avlId: 518, channel: 'state_flags_p4_control', bytes: 8 },
    { avlId: 519, channel: 'state_flags_p4_indicator', bytes: 8 },
    { avlId: 520, channel: 'state_flags_p4_agricultural', bytes: 8 },
    { avlId: 521, channel: 'state_flags_p4_utility', bytes: 8 },
    { avlId: 522, channel: 'state_flags_p4_cistern', bytes: 8 },
    { avlId: 540, channel: 'throttle_position_group', bytes: 1 },
    { avlId: 541, channel: 'commanded_equivalence_ratio', bytes: 1, divisor: 100 },
    { avlId: 543, channel: 'hybrid_system_voltage', bytes: 2 },
    { avlId: 544, channel: 'hybrid_system_current', bytes: 2, signed: true },
    { avlId: 755, channel: 'oem_remaining_distance', bytes: 2 },
    { avlId: 759, channel: 'fuel_type', bytes: 1 },
    { avlId: 855, channel: 'lng_used_total', bytes: 4 },
    { avlId: 856, channel: 'lng_used_counted', bytes: 4 },
    { avlId: 857, channel: 'lng_level_pct', bytes: 2 },
    { avlId: 858, channel: 'lng_level_mass', bytes: 2 },
    { avlId: 1100, channel: 'lpg_used_total', bytes: 4 },
    { avlId: 1101, channel: 'lpg_used_counted', bytes: 4 },
    { avlId: 1102, channel: 'lpg_level_pct', bytes: 2 },
    { avlId: 1103, channel: 'lpg_level_l', bytes: 2 },
    { avlId: 1151, channel: 'oem_battery_state_of_health', bytes: 2 },
    { avlId: 1152, channel: 'oem_battery_temperature', bytes: 2, signed: true },
    ...canStateFlagDescriptors(),
];
/**
 * The 207 pre-decoded CAN booleans (SSF/CSF/ISF/ASF/USF/CiSF), built from the
 * registry's own table so the id and the channel key cannot drift apart. All are one
 * byte, unsigned, 0/1, multiplier 1 — the uniformity is why they are generated rather
 * than transcribed 207 times.
 */
function canStateFlagDescriptors() {
    return CAN_STATE_FLAGS.map((flag) => ({ avlId: flag.avlId, channel: flag.key, bytes: 1 }));
}
const AVL_TABLES = {
    fmb_fmc: FMB_FMC_DESCRIPTORS,
    fm11xy: [],
    fm36: [],
    fm64: [],
    tmt: [],
};
/**
 * Ids that exist on FMB/FMC and are deliberately NOT decoded, with the reason.
 *
 * This table is the point of invariant 5. Each of these decodes to `io_<id>` with its
 * bytes intact, so the day a bench test settles the question the history can be
 * backfilled. Guessing instead would write a plausible wrong number that nobody can
 * tell apart from a right one.
 */
export const AVL_DELIBERATELY_UNMAPPED = new Map([
    [6, 'Analog Input 2 vs SD Status — sources conflict on what id 6 even is'],
    [10, 'SD Status vs Analog Input 2 — the other half of the id 6 conflict'],
    [13, 'Fuel rate: one vendor revision says l/h, the other l/100km. Opposite units'],
    [25, 'BLE temperature 1: ×0.1 with sentinels 2000/3000/4000 vs ×0.01 with 32765–32767'],
    [26, 'BLE temperature 2: same unresolved multiplier/sentinel pair as id 25'],
    [27, 'BLE temperature 3: same unresolved multiplier/sentinel pair as id 25'],
    [28, 'BLE temperature 4: same unresolved multiplier/sentinel pair as id 25'],
    [34, 'Fuel pressure on FMB/FMC but Fuel Level litres on FM64, same width — no vetted row'],
    [39, 'Intake Air Temperature: signedness not stated, and a negative reads as +200 °C'],
    [58, 'Engine oil temperature: type column says unsigned, its own min/max says −128..127'],
    [60, 'Fuel rate: the mirror of the id 13 l/h vs l/100km conflict'],
    [86, 'BLE 1 Humidity: the multiplier cell carries a footnote whose text was never retrieved'],
    [104, 'BLE 2 Humidity: same unretrieved footnote as id 86'],
    [106, 'BLE 3 Humidity: same unretrieved footnote as id 86'],
    [108, 'BLE 4 Humidity: same unretrieved footnote as id 86'],
    [105, 'Total Mileage (counted): the source row was truncated before its width and units'],
    [136, 'Tacho Driver Card Presence: range 0–3 given, meanings of the four values unsourced'],
    [201, 'LLS fuel level: signed/unsigned disputed and units literally "kvants or ltr"'],
    [242, 'ManDown: single upstream, scoped to the TMT250 personal tracker, absent from FMC003'],
    [406, 'Driver Card ID: the vendor description cell is a copy-paste of id 405 (gender)'],
    [407, 'Driver card expiration: declares 1 byte and a maximum of 9999 — mutually impossible'],
    [408, 'Driver Card place of issue: declares 4 bytes and a maximum of 65535, no code table'],
    // ── Phase 4: ids above 255 the research could not settle ──────────────────
    [
        111,
        'AdBlue level: AVL_ID_REFERENCE §1 names 111/112 as AdBlue but no source read states ' +
            'width, or whether the level is percent or litres. That is the 84-vs-89 failure again: ' +
            'a 45 that means 45 % and a 45 that means 4.5 l look identical on a gauge',
    ],
    [
        112,
        'AdBlue level: the other half of the 111 gap — percent vs litres unresolved. The sourced ' +
            'AdBlue signal we do decode is id 987, the low-level warning lamp',
    ],
    [118, 'Axle 1 load: named in AVL_ID_REFERENCE §1, but width, signedness and units (kg vs ' + 'daN) are stated nowhere. An axle load feeds an overload alert, so a scale error is a fine'], // prettier-ignore
    [119, 'Axle 2 load: same unsourced width and units as id 118'],
    [120, 'Axle 3 load: same unsourced width and units as id 118'],
    [121, 'Axle 4 load: same unsourced width and units as id 118'],
    [122, 'Axle 5 load: same unsourced width and units as id 118'],
    [
        258,
        'EcoMaximum: 8 bytes, but a PACKED STRUCT (2B zeros, then X, Y, Z in mg), not a scalar. ' +
            'Reading it as one integer produces a number in no unit at all',
    ],
    [259, 'EcoAverage: the same packed X/Y/Z struct as id 258, not a scalar'],
    [263, 'BT Status: values 0–4 are declared but the source never says what the four mean'],
    [
        282,
        'Fault Codes over the LV-CAN200 adapter: the source type column is literally "-" and no ' +
            'parser implements it. It must NOT be assumed to mirror 281 comma-separated ASCII — see ' +
            'dtc.ts. Stored raw until a capture or the adapter protocol document settles it',
    ],
    [
        285,
        'Blood alcohol content: the described 14-bit/2-bit split does not fit the source’s own ' +
            'maximum of 9999, so the bitfield layout is unverified',
    ],
    [329, 'AIN Speed: MSP500-only and the source carries no description at all for it'],
    [
        387,
        'ISO6709 coordinates: typed HEX but described as ±DDMMSS.SSSS text, and the length cell ' +
            'is nonsense. A coordinate decoded from the wrong reading puts a vehicle in the sea',
    ],
    [388, 'Module ID 17B: 17 opaque bytes with a nonsense maximum; no structure given'],
    [483, 'UL202-02 sensor status: a status code whose enumeration the source never lists'],
    [
        542,
        'Intake MAP: kPa, and this registry has no pressure unit. Relabelling kPa as bars is a ' +
            '100× error; adding a unit is a deliberate decision, not a side effect of a mapping',
    ],
    [
        701,
        'BLE temperature 1 (FMB6XX): Traccar-only, and scoped to a family this map does not ' +
            'decode at all. Ids 702–704 are the same case',
    ],
    [
        10644,
        'Temperature probe 1: Traccar-only naming, ×0.01, while the Dallas probes this platform ' +
            'already decodes are ×0.1. Two probe families with different multipliers and one ' +
            'unverified source is exactly how a −18 °C reefer becomes −1.8 °C. Ids 10645–10649 same',
    ],
    [
        10800,
        'EYE sensor temperature 1: Traccar-only attribute names, absent from the vendor scrape. ' +
            'Ids 10801–10803, 10828–10835 and 11317 are the same single-source case',
    ],
]);
/**
 * Documentation, not a lookup. These are the ids whose meaning changes between device
 * families — the evidence for rule (b), and the reason another family's element list
 * must never be pasted into the table above.
 */
export const AVL_FAMILY_COLLISIONS = [
    {
        avlId: 8,
        fmbFmc: 'Authorized iButton, 8B',
        otherFamilies: 'FM64: Dallas Temperature 5, 2B signed ×0.1 °C',
    },
    {
        avlId: 34,
        fmbFmc: 'Fuel pressure, 2B kPa',
        otherFamilies: 'FM64: Fuel Level litres, 2B ×0.1 — same width, plausible magnitude',
    },
    {
        avlId: 69,
        fmbFmc: 'GNSS Status 0..3',
        otherFamilies: 'FM36: same name, inverted enum — 1 = antenna disconnected, 3 = fix',
    },
    {
        avlId: 84,
        fmbFmc: 'Fuel Level litres, 2B',
        otherFamilies: 'FM64: Accelerator Pedal Position, 4B %',
    },
    { avlId: 87, fmbFmc: 'Total Mileage, 4B m', otherFamilies: 'FM64: Fuel Level, 4B % (max 102)' },
    { avlId: 89, fmbFmc: 'Fuel Level percent, 1B', otherFamilies: 'FM64: Axle weight 1, kg' },
    { avlId: 90, fmbFmc: 'Door Status, 2B bitmask', otherFamilies: 'FM64: Axle weight 2, 2B kg' },
    { avlId: 110, fmbFmc: 'Fuel Rate, 2B l/h', otherFamilies: 'FM64: Diagnostics Supported, 1B' },
    {
        avlId: 113,
        fmbFmc: 'Battery Level, 1B %',
        otherFamilies: 'FM36: LVCAN Engine RPM 2B · FM64: Service Distance 4B signed km',
    },
    { avlId: 236, fmbFmc: 'Alarm, 1B', otherFamilies: 'FM64: Axis X, 2B' },
    { avlId: 238, fmbFmc: 'Bluetooth NMEA receiver MAC, 8B', otherFamilies: 'FM64: Axis Z, 2B' },
    {
        avlId: 243,
        fmbFmc: 'Green driving duration, 2B ms',
        otherFamilies: 'FM64: Idling, 1B boolean — the width differs too, so a naive parser desynchronises',
    },
    { avlId: 245, fmbFmc: 'Gyroscope axis', otherFamilies: 'FM64: Analog Input 4, 2B' },
    {
        avlId: 248,
        fmbFmc: 'Immobilizer, 1B',
        otherFamilies: 'FM64: Geofence Zone Over Speeding — the value is a zone number, not a state',
    },
    { avlId: 251, fmbFmc: 'Idling, 1B', otherFamilies: 'FM11XY/FM36/FM64: Immobilizer, 1B' },
    { avlId: 252, fmbFmc: 'Unplug, 1B', otherFamilies: 'FM11XY/FM36/FM64: Authorized driving, 1B' },
    { avlId: 147, fmbFmc: 'Driver 1 ID High, 8B', otherFamilies: 'FM64: Manual CAN 02' },
];
/** Index: family → id → descriptors, most-constrained first. */
const DESCRIPTOR_INDEX = new Map(AVL_FAMILIES.map((family) => {
    const byId = new Map();
    for (const descriptor of AVL_TABLES[family]) {
        const bucket = byId.get(descriptor.avlId);
        if (bucket === undefined)
            byId.set(descriptor.avlId, [descriptor]);
        else
            bucket.push(descriptor);
    }
    for (const bucket of byId.values()) {
        bucket.sort((a, b) => compareFirmware(b.firmwareMin ?? '', a.firmwareMin ?? ''));
    }
    return [family, byId];
}));
/**
 * Compares Teltonika dotted firmware strings ('03.27.07.Rev.00', '01.00.24').
 * Segments are read until the first non-numeric one, so the trailing `.Rev.NN` is
 * ignored rather than parsed as NaN and silently sorted to the bottom.
 */
export function compareFirmware(a, b) {
    const left = firmwareSegments(a);
    const right = firmwareSegments(b);
    const length = Math.max(left.length, right.length);
    for (let i = 0; i < length; i += 1) {
        const diff = (left[i] ?? 0) - (right[i] ?? 0);
        if (diff !== 0)
            return diff < 0 ? -1 : 1;
    }
    return 0;
}
function firmwareSegments(version) {
    const out = [];
    for (const part of version.split('.')) {
        if (!/^\d+$/.test(part))
            break;
        out.push(Number(part));
    }
    return out;
}
/**
 * The descriptor for an element, or undefined when nothing is mapped. Firmware-
 * constrained rows win when satisfied; an unknown firmware never satisfies one.
 */
export function lookupAvlDescriptor(context, avlId) {
    const candidates = DESCRIPTOR_INDEX.get(context.family)?.get(avlId);
    if (candidates === undefined)
        return undefined;
    const firmware = context.firmware ?? null;
    for (const candidate of candidates) {
        if (candidate.firmwareMin === undefined)
            return candidate;
        if (firmware === null)
            continue;
        if (compareFirmware(firmware, candidate.firmwareMin) >= 0)
            return candidate;
    }
    return undefined;
}
/**
 * The channel key for an element nothing maps. Stable and parseable, so a later
 * migration can rename `io_47` to its real channel once the id is understood.
 */
export function channelKeyForUnmappedAvlId(avlId) {
    return `io_${avlId}`;
}
/**
 * Decodes one AVL element. Never throws: a decode that throws at frame rate takes an
 * entire batch of good records down with the one element nobody has mapped yet.
 */
export function decodeAvlElement(context, avlId, rawBytes) {
    const descriptor = lookupAvlDescriptor(context, avlId);
    const hex = bytesToHex(rawBytes);
    if (descriptor === undefined) {
        return {
            avlId,
            channel: channelKeyForUnmappedAvlId(avlId),
            mapped: false,
            value: null,
            rawValue: readInteger(rawBytes, false),
            rawBytes,
            hex,
            text: null,
            outOfRange: false,
            widthMismatch: false,
        };
    }
    const rawValue = readInteger(rawBytes, descriptor.signed ?? false);
    const valueType = getChannel(descriptor.channel)?.valueType ?? 'number';
    const text = valueType === 'text' ? asciiFromBytes(rawBytes) : null;
    const value = physicalValue(rawValue, descriptor, valueType);
    return {
        avlId,
        channel: descriptor.channel,
        mapped: true,
        value,
        rawValue,
        rawBytes,
        hex,
        text,
        outOfRange: outOfRangeFor(descriptor, value),
        widthMismatch: descriptor.bytes !== undefined && descriptor.bytes !== rawBytes.length,
    };
}
/**
 * The same decode, taking a parsed I/O element straight from the codec.
 *
 * The parameter is declared structurally rather than importing the codec's type, so
 * this module stays free of a dependency on the framing layer: the map is about
 * meaning, the codec is about bytes, and only one of the two changes when Teltonika
 * publishes a new id.
 */
export function decodeAvlIoElement(context, element) {
    return decodeAvlElement(context, element.id, element.value);
}
function physicalValue(rawValue, descriptor, valueType) {
    // Opaque keys and text have no numeric meaning. Rendering an 8-byte iButton as a
    // float silently rounds it, and two different keys can round to the same number.
    if (valueType === 'opaque' || valueType === 'text')
        return null;
    if (rawValue === null)
        return null;
    if (rawValue > BigInt(Number.MAX_SAFE_INTEGER) || rawValue < BigInt(Number.MIN_SAFE_INTEGER)) {
        return null;
    }
    const divisor = descriptor.divisor ?? 1;
    return divisor === 1 ? Number(rawValue) : Number(rawValue) / divisor;
}
function outOfRangeFor(descriptor, value) {
    if (value === null)
        return false;
    const override = descriptor.valueRange;
    if (override !== undefined)
        return value < override.min || value > override.max;
    return isOutOfRange(descriptor.channel, value);
}
/**
 * Big-endian two's complement over the width the framing supplied — not the width
 * the table claims. Returns null past 8 bytes, where BigInt would still work but no
 * variable-length element has integer semantics anyway.
 */
function readInteger(rawBytes, signed) {
    if (rawBytes.length === 0 || rawBytes.length > 8)
        return null;
    let value = 0n;
    for (const byte of rawBytes)
        value = (value << 8n) | BigInt(byte);
    if (!signed)
        return value;
    const bits = BigInt(rawBytes.length * 8);
    const signBit = 1n << (bits - 1n);
    return (value & signBit) === 0n ? value : value - (1n << bits);
}
const HEX_DIGITS = '0123456789ABCDEF';
/**
 * Hand-rolled because this package is bundled for the browser as well as Node:
 * Buffer does not exist there and the DOM lib is deliberately off.
 */
export function bytesToHex(bytes) {
    let out = '';
    for (const byte of bytes) {
        out += HEX_DIGITS[(byte >> 4) & 0x0f];
        out += HEX_DIGITS[byte & 0x0f];
    }
    return out;
}
/** Inverse of bytesToHex. Returns null on anything that is not clean hex. */
export function hexToBytes(hex) {
    if (hex.length % 2 !== 0)
        return null;
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i += 1) {
        const high = HEX_DIGITS.indexOf(hex[i * 2].toUpperCase());
        const low = HEX_DIGITS.indexOf(hex[i * 2 + 1].toUpperCase());
        if (high < 0 || low < 0)
            return null;
        out[i] = (high << 4) | low;
    }
    return out;
}
/**
 * ASCII only, and by hand: TextDecoder is a DOM/Node global this package must not
 * touch. Teltonika pads text elements with NULs and wraps driver names in '$'.
 */
function asciiFromBytes(bytes) {
    let out = '';
    for (const byte of bytes) {
        if (byte === 0 || byte === 0x24)
            continue;
        out += String.fromCharCode(byte);
    }
    return out.trim();
}
/** Every id the FMB/FMC table decodes. Sorted, for tests and for documentation. */
export function mappedAvlIds(family = 'fmb_fmc') {
    return [...new Set(AVL_TABLES[family].map((d) => d.avlId))].sort((a, b) => a - b);
}
//# sourceMappingURL=avl-map.js.map